import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";
import { useSession } from "../useSession";

type Recall = {
  id: string;
  patient_id: string;
  patient_name: string;
  due_date: string;
  days_overdue: number;
  status: string;
  requested_by_name: string;
  contact_attempts: number;
  last_contacted_at: string | null;
  contact_note: string;
  // Absent entirely for the front desk and a clinic administrator. Not blank:
  // absent, so nothing can render an empty field and imply there was nothing
  // to say (ADR-018 point 3).
  reason_text?: string;
};

function overdue(days: number) {
  if (days < 0) return `due in ${Math.abs(days)} days`;
  if (days === 0) return "due today";
  if (days < 14) return `${days} days overdue`;
  if (days < 60) return `${Math.round(days / 7)} weeks overdue`;
  return `${Math.round(days / 30)} months overdue`;
}

/** Which of the two actions that need a sentence is open on a row. */
type Asking = { id: string; what: "contact" | "cancel" } | null;

/**
 * Patients the clinic said it would bring back.
 *
 * The list comes in two shapes and the server decides which. A doctor or a
 * nurse sees why each patient is coming back. The front desk sees who and
 * when, and never why: "review diabetes control" is a diagnosis written into a
 * task, and the wall in ADR-008 has no exception in it.
 *
 * The front desk can record a call, which counts and closes nothing. Only a
 * doctor closes a recall, and only after the work is actually done, because a
 * recall closed by an unrelated visit records work nobody did.
 *
 * Nothing ages out. The most overdue sits at the top.
 *
 * ## Three actions that are not equals
 *
 * They used to be three identical buttons in a row. Recording a call happens
 * many times a day, marking one done happens when the patient has actually
 * been seen, and stopping one almost never. A row that draws them the same
 * says they carry the same weight, and the one that needs a deliberate aim is
 * the one that ends the clinic's attempt to bring somebody back.
 */
export function Recalls() {
  const { clinic } = useSession();
  const [rows, setRows] = useState<Recall[]>([]);
  const [reasonVisible, setReasonVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [asking, setAsking] = useState<Asking>(null);
  const [said, setSaid] = useState("");
  const box = useRef<HTMLInputElement>(null);

  const isDoctor = clinic?.role === "doctor";

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: failed } = await api.GET("/portal/recalls", {
      params: { header: clinicHeader() },
    });
    setLoading(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setRows((data?.items ?? []) as Recall[]);
    setReasonVisible(Boolean(data?.reason_visible));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function ask(row: Recall, what: "contact" | "cancel") {
    setAsking({ id: row.id, what });
    setSaid(what === "contact" ? "No answer." : "");
    window.setTimeout(() => box.current?.select(), 0);
  }

  const act = async (row: Recall, what: "contact" | "complete" | "cancel") => {
    // Stopping needs a reason and the server keeps it. An empty one is not a
    // reason, so the button stays disabled rather than sending a blank.
    if (what === "cancel" && !said.trim()) return;

    setBusy(row.id);
    // Each endpoint separately rather than through a computed path: completing
    // takes no body at all, and a union would cast that difference away.
    const params = { path: { id: row.id }, header: clinicHeader() };
    const { error: failed } =
      what === "contact"
        ? await api.POST("/portal/recalls/{id}/contact", {
            params,
            body: { note: said.trim() || "No answer." },
          })
        : what === "complete"
          ? await api.POST("/portal/recalls/{id}/complete", { params })
          : await api.POST("/portal/recalls/{id}/cancel", {
              params,
              body: { reason: said.trim() },
            });
    setBusy(null);
    setAsking(null);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    await load();
  };

  if (loading) return <p className="empty">Reading the list…</p>;

  return (
    <section className="recalls">
      <header className="screen-band">
        <div className="screen-band-photo" aria-hidden="true" />
        <div className="screen-band-words">
          <h1>Patients to bring back</h1>
          <p className="screen-lead">
            Most overdue first. Nothing leaves this list until a doctor says
            the work is done.
            {!reasonVisible && " Your role does not show why each patient is returning."}
          </p>
        </div>
      </header>

      {/* The other way a clinic brings people back, and the one nobody plans
          for: a manufacturer recalls a lot and the question is who had it.
          It is off the navigation because it is not a daily screen, and it
          is here because this is the screen it belongs beside. */}
      <p className="lead">
        <Link to="/batches">Find who received a vaccine batch</Link>
      </p>

      <ErrorNote message={error} />

      {rows.length === 0 && (
        <p className="empty">
          Nobody is waiting to be brought back. A doctor adds a patient here from
          their chart.
        </p>
      )}

      <ul className="recall-list">
        {rows.map((row) => {
          const late = row.days_overdue > 0;
          const open = asking?.id === row.id ? asking.what : null;
          return (
            <li key={row.id} className={late ? "recall late" : "recall"}>
              <div className="recall-when">
                <span className="recall-overdue">{overdue(row.days_overdue)}</span>
                <span className="recall-by">set by {row.requested_by_name}</span>
              </div>

              <div className="recall-who">
                <Link to={`/patients/${row.patient_id}`} className="recall-name">
                  {row.patient_name}
                </Link>

                {/* The doctor's own sentence, only when the server sent it.
                    It is what makes the row actionable to somebody who was
                    not in the room. */}
                {row.reason_text && <p className="recall-reason">{row.reason_text}</p>}

                {row.contact_attempts > 0 && (
                  <p className="recall-chased">
                    {row.contact_attempts} contact attempt
                    {row.contact_attempts === 1 ? "" : "s"}
                    {row.contact_note && ` · ${row.contact_note}`}
                  </p>
                )}
              </div>

              {/* Three weights, because these are not three equal acts. */}
              <div className="recall-actions">
                <button
                  type="button"
                  disabled={busy === row.id}
                  onClick={() => ask(row, "contact")}
                >
                  Record a call
                </button>

                {isDoctor && (
                  <>
                    <button
                      type="button"
                      className="quiet"
                      disabled={busy === row.id}
                      onClick={() => void act(row, "complete")}
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      className="link danger"
                      disabled={busy === row.id}
                      onClick={() => ask(row, "cancel")}
                    >
                      Stop
                    </button>
                  </>
                )}
              </div>

              {open && (
                <form
                  className="recall-ask"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void act(row, open);
                  }}
                >
                  <label htmlFor={`say-${row.id}`}>
                    {open === "contact"
                      ? `What happened when you called ${row.patient_name}?`
                      : `Why stop trying to bring ${row.patient_name} back?`}
                  </label>
                  <div className="recall-ask-row">
                    <input
                      id={`say-${row.id}`}
                      ref={box}
                      value={said}
                      onChange={(event) => setSaid(event.target.value)}
                    />
                    <button
                      type="submit"
                      className={open === "cancel" ? "danger" : undefined}
                      disabled={busy === row.id || (open === "cancel" && !said.trim())}
                    >
                      {busy === row.id
                        ? "Saving…"
                        : open === "contact"
                          ? "Log the call"
                          : "Stop the recall"}
                    </button>
                    <button type="button" className="quiet" onClick={() => setAsking(null)}>
                      Cancel
                    </button>
                  </div>
                  <p className="recall-ask-note">
                    {open === "contact"
                      ? "This counts the attempt. The recall stays open."
                      : "The row stays on record with your reason against it."}
                  </p>
                </form>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
